import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import type { CurrentPage } from '../../App';
import { v4 as uuidv4 } from 'uuid';

// Helper component for form sections
const FormSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-6 p-4 border border-gray-200 rounded-lg shadow-sm bg-white">
    <h3 className="text-lg font-semibold mb-4 text-gray-700 border-b pb-2">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {children}
    </div>
  </div>
);

// Helper component for input fields
const InputField: React.FC<{ label: string; name: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; type?: string; placeholder?: string }> =
 ({ label, name, value, onChange, type = 'text', placeholder }) => (
  <div className="mb-2">
    <label htmlFor={name} className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
    <input
      type={type}
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder || `Enter ${label.toLowerCase()}...`}
      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white text-gray-900"
    />
  </div>
);

// Helper component for Textarea fields
const TextAreaField: React.FC<{ label: string; name: string; value: string; onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void; rows?: number; placeholder?: string }> =
 ({ label, name, value, onChange, rows = 3, placeholder }) => (
  <div className="mb-2 md:col-span-2"> {/* Span across 2 columns in grid */}
    <label htmlFor={name} className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
    <textarea
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      rows={rows}
      placeholder={placeholder || `Enter ${label.toLowerCase()}...`}
      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white text-gray-900"
    />
  </div>
);

interface FloorAreaRow {
  id: string; // Unique ID for React key prop
  name: string;
  height: string;
  area: string;
  hlp: string;
  pw: string;
}

interface FloorAreasState {
  groundFloor: FloorAreaRow[];
  firstFloor: FloorAreaRow[];
}

// Initial state for the floor areas table
const initialFloorAreasState: FloorAreasState = {
  groundFloor: [
    { id: 'gf_main', name: 'Main House', height: '', area: '', hlp: '', pw: '' },
    { id: 'gf_ext1', name: 'Ext 1', height: '', area: '', hlp: '', pw: '' },
    { id: 'gf_ext2', name: 'Ext 2', height: '', area: '', hlp: '', pw: '' },
    { id: 'gf_ext3', name: 'Ext 3', height: '', area: '', hlp: '', pw: '' },
    { id: 'gf_ext4', name: 'Ext 4', height: '', area: '', hlp: '', pw: '' },
  ],
  firstFloor: [
    { id: 'ff_main', name: 'Main House', height: '', area: '', hlp: '', pw: '' },
    { id: 'ff_ext1', name: 'Ext 1', height: '', area: '', hlp: '', pw: '' },
    { id: 'ff_ext2', name: 'Ext 2', height: '', area: '', hlp: '', pw: '' },
    { id: 'ff_ext3', name: 'Ext 3', height: '', area: '', hlp: '', pw: '' },
    { id: 'ff_ext4', name: 'Ext 4', height: '', area: '', hlp: '', pw: '' },
  ],
};

// Interface for a single row in the Room Details table
interface RoomDetailRow {
  id: string; // Unique ID for React key prop and row management
  roomId: string;
  h: string; // Height
  l: string; // Length
  w: string; // Width
  lightingXO: string; // Lighting X/O (Lights On / Off ?) -> Treat as string for flexibility
  radLXH: string; // Radiator L x H -> Treat as string
  windowH: string;
  windowW: string;
}

export interface AssessorSiteNotesProps {
  onNavigate: (page: CurrentPage, associatedId?: string) => void;
  floorPlanId?: string;
}

const AssessorSiteNotes: React.FC<AssessorSiteNotesProps> = ({ onNavigate, floorPlanId }) => {
  const [formData, setFormData] = useState({
    address: '',
    propertyType: '',
    detachment: '',
    ageBand: '',
    groundLevel: '',
    roofTypeRdSAP: '',
    wallType: '',
    partyWall: '',
    wallThickness: '',
    roofType: '',
    roofInsulation: '',
    heatingType: '',
    heatingInfo: '',
    totalRadsHeaters: '',
    trvs: '',
    tRad: '',
    heatingSourceLocation: '',
    tankLocation: '',
    totalWindows: '',
    windowType: '',
    glazingThickness: '',
    windowsMissingTrickleVents: '',
    mevsKitchen: '',
    mevsBathroom: '',
    mevsEnsuite: '',
    mevsWC: '',
    mevsUtility: '',
    doorUCut: '',
    // Additional ventilation fields
    openChimneys: '0',
    openFlues: '0',
    openChimneysFluesAttachedToClose: '0',
    fluesAttachedToSolidFuelBoiler: '0',
    openFluesAttachedToOtherHeater: '0',
    blockedChimneys: '0',
    intermittentExtractFans: '0',
    passiveVents: '0',
    fluelessGasFires: '0',
    fixedSpaceCooling: '',
    draughtLobby: '',
    gmLocation: '',
    emLocations: '',
    sTapLocation: '',
    additionalNotes: '',
    wallInsulationType: '',
    wallInsulationElevationsCount: '',
    wallInsulationElevations: [],
  });
  const [floorAreas, setFloorAreas] = useState<FloorAreasState>(initialFloorAreasState);
  const [roomDetails, setRoomDetails] = useState<RoomDetailRow[]>([]); // Initialize as empty array

  useEffect(() => {
    if (floorPlanId) {
      console.log('AssessorSiteNotes received floorPlanId:', floorPlanId);
      // Logic to load/use site notes for this floorPlanId
    }
  }, [floorPlanId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handler for changes within the Floor Areas table
  const handleFloorAreaChange = (
    floor: keyof FloorAreasState,
    rowIndex: number,
    field: keyof Omit<FloorAreaRow, 'id' | 'name'>, // Field being changed (height, area, etc.)
    value: string
  ) => {
    setFloorAreas(prev => {
      // Create a deep copy to avoid state mutation issues
      const newState = {
          groundFloor: prev.groundFloor.map(row => ({...row})),
          firstFloor: prev.firstFloor.map(row => ({...row})),
      };
      // Update the specific cell
      if (newState[floor] && newState[floor][rowIndex]) {
          (newState[floor][rowIndex] as any)[field] = value;
      }
      return newState;
    });
  };

  // Handler for changes within the Room Details table
   const handleRoomDetailChange = (
     rowIndex: number,
     field: keyof Omit<RoomDetailRow, 'id'>, // Field being changed
     value: string
   ) => {
     setRoomDetails(prev => {
       const newDetails = [...prev];
       if (newDetails[rowIndex]) {
         // Create a new object for the row to ensure state update triggers re-render
         newDetails[rowIndex] = { ...newDetails[rowIndex], [field]: value };
       }
       return newDetails;
     });
   };

   // Function to add a new row to the Room Details table
   const addRoomDetailRow = () => {
     setRoomDetails(prev => [
       ...prev,
       // Add a new row with default empty values and a unique ID
       {
         id: uuidv4(), // Generate unique ID
         roomId: '',
         h: '',
         l: '',
         w: '',
         lightingXO: '',
         radLXH: '',
         windowH: '',
         windowW: '',
       }
     ]);
   };

   // Function to remove a row from the Room Details table by its index
   const removeRoomDetailRow = (indexToRemove: number) => {
     setRoomDetails(prev => prev.filter((_, index) => index !== indexToRemove));
   };

   // Calculate Totals using useMemo for efficiency
   const { totalLightsOn, totalLightsOff, totalLEL } = useMemo(() => {
       let lightsOn = 0;
       let lightsOff = 0;
       let lel = 0; // Assuming LEL is a simple count or sum from a field, adjust if needed

       roomDetails.forEach(row => {
           // Example: Assuming lightingXO is like "3/1" (On/Off)
           const parts = row.lightingXO.split('/');
           if (parts.length === 2) {
               lightsOn += parseInt(parts[0], 10) || 0;
               lightsOff += parseInt(parts[1], 10) || 0;
           }
           // Example: Assuming LEL is just the 'l' value - ADJUST THIS LOGIC based on actual meaning
           lel += parseFloat(row.l) || 0;
       });

       return { totalLightsOn: lightsOn, totalLightsOff: lightsOff, totalLEL: lel };
   }, [roomDetails]); // Recalculate only when roomDetails changes

  // Function to render one floor section of the table
  const renderFloorSection = (floorName: string, floorKey: keyof FloorAreasState) => (
    <div className="mb-4">
      <h4 className="text-md font-semibold mb-2 text-gray-600">{floorName}</h4>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Location</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Height</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Area</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HLP</th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">P/W</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {floorAreas[floorKey].map((row, index) => (
              <tr key={row.id}>
                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-700">{row.name}</td>
                <td className="px-1 py-1 whitespace-nowrap">
                  <input
                    type="text" // Use text for flexibility, could be number
                    value={row.height}
                    onChange={(e) => handleFloorAreaChange(floorKey, index, 'height', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </td>
                <td className="px-1 py-1 whitespace-nowrap">
                  <input
                    type="text"
                    value={row.area}
                    onChange={(e) => handleFloorAreaChange(floorKey, index, 'area', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </td>
                <td className="px-1 py-1 whitespace-nowrap">
                  <input
                    type="text"
                    value={row.hlp}
                    onChange={(e) => handleFloorAreaChange(floorKey, index, 'hlp', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </td>
                <td className="px-1 py-1 whitespace-nowrap">
                  <input
                    type="text"
                    value={row.pw}
                    onChange={(e) => handleFloorAreaChange(floorKey, index, 'pw', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderContent = () => (
    <>
      <FormSection title="Property Details">
        <InputField label="Address" name="address" value={formData.address} onChange={handleChange} />
        <InputField label="Detachment" name="detachment" value={formData.detachment} onChange={handleChange} />
        <InputField label="Age Band" name="ageBand" value={formData.ageBand} onChange={handleChange} />
      </FormSection>

      <FormSection title="Wall & Roof Info">
        <InputField label="Wall Type" name="wallType" value={formData.wallType} onChange={handleChange} />
        <InputField label="Party Wall" name="partyWall" value={formData.partyWall} onChange={handleChange} />
        <InputField label="Wall Thickness" name="wallThickness" value={formData.wallThickness} onChange={handleChange} />
        <InputField label="Roof Type" name="roofType" value={formData.roofType} onChange={handleChange} />
        <InputField label="Roof Insulation" name="roofInsulation" value={formData.roofInsulation} onChange={handleChange} />
      </FormSection>

      <FormSection title="Heating">
        <InputField label="Heating Type" name="heatingType" value={formData.heatingType} onChange={handleChange} placeholder="e.g., Gas Boiler, ASHP" />
        
        <InputField label="Heating Info" name="heatingInfo" value={formData.heatingInfo} onChange={handleChange} placeholder="e.g., Vaillant ecoTEC" />

        <InputField label="Total Radiators/Heaters" name="totalRadsHeaters" value={formData.totalRadsHeaters} onChange={handleChange} />
        <InputField label="TRVs (Thermostatic Radiator Valves)" name="trvs" value={formData.trvs} onChange={handleChange} />
        <InputField label="T-Rad" name="tRad" value={formData.tRad} onChange={handleChange} />
        <InputField label="Heating Source Location" name="heatingSourceLocation" value={formData.heatingSourceLocation} onChange={handleChange} />
        <InputField label="Tank Location (If any)" name="tankLocation" value={formData.tankLocation} onChange={handleChange} />
      </FormSection>

      <FormSection title="Windows & Ventilation">
        <InputField label="Total Windows" name="totalWindows" value={formData.totalWindows} onChange={handleChange} />
        <InputField label="Window Type" name="windowType" value={formData.windowType} onChange={handleChange} />
        <InputField label="Glazing Thickness" name="glazingThickness" value={formData.glazingThickness} onChange={handleChange} placeholder="e.g., 6 / 12 / 16" />
        <InputField label="Windows Missing Trickle Vents" name="windowsMissingTrickleVents" value={formData.windowsMissingTrickleVents} onChange={handleChange} placeholder="e.g., Yes / No / Count" />
      </FormSection>

      <FormSection title="MEVS & Vents">
        <InputField label="Kitchen" name="mevsKitchen" value={formData.mevsKitchen} onChange={handleChange} placeholder="Yes / No / Details" />
        <InputField label="Bathroom" name="mevsBathroom" value={formData.mevsBathroom} onChange={handleChange} placeholder="Yes / No / Details" />
        <InputField label="Ensuite" name="mevsEnsuite" value={formData.mevsEnsuite} onChange={handleChange} placeholder="Yes / No / Details" />
        <InputField label="WC" name="mevsWC" value={formData.mevsWC} onChange={handleChange} placeholder="Yes / No / Details" />
        <InputField label="Utility" name="mevsUtility" value={formData.mevsUtility} onChange={handleChange} placeholder="Yes / No / Details" />
        <InputField label="Door U/Cut" name="doorUCut" value={formData.doorUCut} onChange={handleChange} placeholder="Yes / No / Measurement" />
      </FormSection>

      <FormSection title="Locations">
        <InputField label="G/M Location" name="gmLocation" value={formData.gmLocation} onChange={handleChange} />
        <InputField label="E/M Locations" name="emLocations" value={formData.emLocations} onChange={handleChange} />
        <InputField label="S-TAP Location" name="sTapLocation" value={formData.sTapLocation} onChange={handleChange} />
      </FormSection>

      {renderFloorSection('Ground Floor', 'groundFloor')}
      {renderFloorSection('First Floor', 'firstFloor')}

      <FormSection title="Room Details">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room ID</th>
                <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">(H)</th>
                <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">(L)</th>
                <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">(W)</th>
                <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lighting X/O</th>
                <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rad LXH</th>
                <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Window (H)</th>
                <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Window (W)</th>
                <th scope="col" className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {roomDetails.map((row, index) => (
                <tr key={row.id}>
                  <td className="px-1 py-1"><input type="text" value={row.roomId} onChange={(e) => handleRoomDetailChange(index, 'roomId', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"/></td>
                  <td className="px-1 py-1"><input type="text" value={row.h} onChange={(e) => handleRoomDetailChange(index, 'h', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"/></td>
                  <td className="px-1 py-1"><input type="text" value={row.l} onChange={(e) => handleRoomDetailChange(index, 'l', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"/></td>
                  <td className="px-1 py-1"><input type="text" value={row.w} onChange={(e) => handleRoomDetailChange(index, 'w', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"/></td>
                  <td className="px-1 py-1"><input type="text" value={row.lightingXO} onChange={(e) => handleRoomDetailChange(index, 'lightingXO', e.target.value)} placeholder="e.g., 3/1" className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"/></td>
                  <td className="px-1 py-1"><input type="text" value={row.radLXH} onChange={(e) => handleRoomDetailChange(index, 'radLXH', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"/></td>
                  <td className="px-1 py-1"><input type="text" value={row.windowH} onChange={(e) => handleRoomDetailChange(index, 'windowH', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"/></td>
                  <td className="px-1 py-1"><input type="text" value={row.windowW} onChange={(e) => handleRoomDetailChange(index, 'windowW', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"/></td>
                  <td className="px-2 py-1 text-center">
                    <button
                      type="button"
                      onClick={() => removeRoomDetailRow(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete Row"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex justify-start">
          <button
            type="button"
            onClick={addRoomDetailRow}
            className="flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus size={18} className="-ml-1 mr-1" />
            Add Room
          </button>
        </div>
        <div className="mt-6 border-t pt-4 text-right space-y-1">
          <p className="text-sm font-medium text-gray-700">Total Lights = X/O: <span className="font-semibold text-gray-900">{totalLightsOn} / {totalLightsOff}</span></p>
          <p className="text-sm font-medium text-gray-700">Total LEL = X: <span className="font-semibold text-gray-900">{totalLEL.toFixed(2)}</span></p>
        </div>
      </FormSection>

      <FormSection title="Additional Notes">
        <TextAreaField label="Additional Notes" name="additionalNotes" value={formData.additionalNotes} onChange={handleChange} rows={5} />
      </FormSection>
    </>
  );

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-md p-4 flex items-center justify-between sticky top-0 z-40">
        <button onClick={() => onNavigate({ page: 'retrofit' })} className="p-2 rounded-lg hover:bg-gray-100">
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
        <h1 className="text-xl font-bold text-gray-800">Assessor Site Notes</h1>
        <div className="w-10"></div> {/* Spacer */}
      </header>

      {/* Main Content */}
      <main className="p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          {renderContent()}

          {/* Action Buttons */}
          <div className="mt-8 flex justify-end space-x-4">
            <button
              type="button"
              className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              Save Notes
            </button>
          </div>
        </div>
      </main>

      {/* Footer Padding */}
      <div className="h-10 flex-shrink-0"></div>


    </div>
  );
};

export default AssessorSiteNotes; 